import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  FlightTakeoff as FlightIcon,
  Schedule as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Person as PilotIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth0 } from '@auth0/auth0-react';
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import styles from '../../styles/Analytics.module.css';

/**
 * FlightAnalytics Page
 *
 * Displays comprehensive flight log analytics from actual logged flights:
 * - Total flights and flight hours (from flight logs)
 * - Average flight duration
 * - Flight trends over time
 * - Most active pilots (by flight hours)
 * - Most used drones (by flight hours)
 *
 * Access: Owner, Admin, Manager roles only
 */
function FlightAnalytics() {
  const { currentOrganization, userRole } = useOrganization();
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30'); // days
  const [analytics, setAnalytics] = useState({
    totalFlights: 0,
    totalFlightHours: 0,
    averageFlightDuration: 0,
    mostActivePilots: [],
    mostUsedDrones: [],
    flightsByDay: []
  });

  // Check if user has permission to view analytics
  const canView = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());

  // Fetch analytics data
  useEffect(() => {
    if (!currentOrganization?.organizationId || !canView) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();

        // Fetch flight log analytics for this organization

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/analytics/flight-logs`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              organizationId: currentOrganization.organizationId,
              timeRangeDays: parseInt(timeRange)
            })
          }
        );


        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch flight log analytics');
        }

        const data = await response.json();

        // Set analytics data directly from backend
        setAnalytics({
          totalFlights: data.totalFlights,
          totalFlightHours: data.totalFlightHours,
          averageFlightDuration: data.averageFlightDuration,
          mostActivePilots: data.mostActivePilots,
          mostUsedDrones: data.mostUsedDrones,
          flightsByDay: data.flightsByDay
        });
      } catch (err) {
        setError('Failed to load flight analytics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [currentOrganization, timeRange, canView, getAccessTokenSilently]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  if (!canView) {
    return (
      <OrganizationLayout>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            You do not have permission to view this page. Only Owners, Admins, and Managers can access analytics.
          </Alert>
        </Box>
      </OrganizationLayout>
    );
  }

  return (
    <OrganizationLayout>
      <Container maxWidth="xl" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 2 : 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 2 : 0 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FlightIcon sx={{ fontSize: isMobile ? 24 : 32, color: '#1976d2' }} />
              <Typography variant={isMobile ? "h5" : "h4"} component="h1">
                Flight Analytics
              </Typography>
            </Box>
            <Typography variant={isMobile ? "body2" : "body1"} color="textSecondary">
              Analytics based on actual logged flight hours and operations.
            </Typography>
          </Box>

          {/* Time Range Filter */}
          <FormControl sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="7">Last 7 Days</MenuItem>
              <MenuItem value="30">Last 30 Days</MenuItem>
              <MenuItem value="90">Last 90 Days</MenuItem>
              <MenuItem value="365">Last Year</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Key Metrics Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
              {/* Total Flights */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <FlightIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Total Flights
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {analytics.totalFlights}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Last {timeRange} days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Flight Hours */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <TimeIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Flight Hours
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {(analytics.totalFlightHours || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Total hours flown
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Authorized Flights */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <TrendingUpIcon color="success" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Authorized
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: '#4caf50', fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {analytics.authorizedFlights}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Successfully authorized
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Average Duration */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <CalendarIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Avg Duration
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {(analytics.averageFlightDuration || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Minutes per flight
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Flight Activity */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: isMobile ? 1.5 : 3 }}>
              {/* Recent Flight Activity */}
              <Grid item xs={12}>
                <Card elevation={2}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 1.5 : 2, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                      Flight Activity (Last 14 Days)
                    </Typography>
                    <Box sx={{ maxHeight: isMobile ? 200 : 250, overflowY: 'auto' }}>
                      {analytics.flightsByDay.map(({ date, count }) => (
                        <Box
                          key={date}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: isMobile ? 1 : 1.5,
                            p: isMobile ? 0.75 : 1,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                            {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: `${count * (isMobile ? 15 : 20)}px`,
                                maxWidth: isMobile ? 100 : 150,
                                height: isMobile ? 4 : 6,
                                bgcolor: '#1976d2',
                                borderRadius: 1
                              }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: isMobile ? 20 : 30, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                              {count}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Most Active Pilots & Most Used Drones */}
            <Grid container spacing={isMobile ? 1.5 : 3}>
              {/* Most Active Pilots */}
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1.5 : 2 }}>
                      <PilotIcon color="primary" sx={{ fontSize: isMobile ? 20 : 24 }} />
                      <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                        Most Active Pilots
                      </Typography>
                    </Box>
                    {isMobile ? (
                      <Stack spacing={1}>
                        {analytics.mostActivePilots.length > 0 ? (
                          analytics.mostActivePilots.map((pilot, index) => (
                            <Card key={pilot.id || index} variant="outlined">
                              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label={`#${index + 1}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                      {pilot.name}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      Flights:
                                    </Typography>
                                    <Chip label={pilot.count} size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      Hours:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                      {pilot.hours.toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                              No flight data available
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Pilot</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Flights</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Hours</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analytics.mostActivePilots.length > 0 ? (
                              analytics.mostActivePilots.map((pilot, index) => (
                                <TableRow key={pilot.id || index}>
                                  <TableCell>#{index + 1}</TableCell>
                                  <TableCell>{pilot.name}</TableCell>
                                  <TableCell align="right">
                                    <Chip label={pilot.count} size="small" color="primary" />
                                  </TableCell>
                                  <TableCell align="right">
                                    {pilot.hours.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  <Typography variant="body2" color="textSecondary">
                                    No flight data available
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Most Used Drones */}
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1.5 : 2 }}>
                      <FlightIcon color="primary" sx={{ fontSize: isMobile ? 20 : 24 }} />
                      <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                        Most Used Drones
                      </Typography>
                    </Box>
                    {isMobile ? (
                      <Stack spacing={1}>
                        {analytics.mostUsedDrones.length > 0 ? (
                          analytics.mostUsedDrones.map((drone, index) => (
                            <Card key={drone.id || index} variant="outlined">
                              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label={`#${index + 1}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                      {drone.name}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      Flights:
                                    </Typography>
                                    <Chip label={drone.count} size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                      Hours:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                      {drone.hours.toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                              No drone data available
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Drone</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Flights</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Hours</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analytics.mostUsedDrones.length > 0 ? (
                              analytics.mostUsedDrones.map((drone, index) => (
                                <TableRow key={drone.id || index}>
                                  <TableCell>#{index + 1}</TableCell>
                                  <TableCell>{drone.name}</TableCell>
                                  <TableCell align="right">
                                    <Chip label={drone.count} size="small" color="primary" />
                                  </TableCell>
                                  <TableCell align="right">
                                    {drone.hours.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  <Typography variant="body2" color="textSecondary">
                                    No drone data available
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </OrganizationLayout>
  );
}

export default FlightAnalytics;
